import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { CompanyCheckResult } from '@/services/organization.service';
import { validateCompanyNameAsync } from '@/services/field-validation.service';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { logger } from '@/utils/logger';
import { config } from '@/config/environment';

interface CompanyNameInputProps {
  value: string;
  onChange: (value: string) => void;
  onDuplicateDetected?: (isDuplicate: boolean) => void;
}

export function CompanyNameInput({ value, onChange, onDuplicateDetected }: CompanyNameInputProps) {
  const [checkResult, setCheckResult] = useState<CompanyCheckResult | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    if (!value || value.trim().length < 3) {
      setCheckResult(null);
      onDuplicateDetected?.(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsChecking(true);
      try {
        const validationResult = await validateCompanyNameAsync(value);
        
        // Convert validation result to check result format
        if (!validationResult.isValid && validationResult.error?.includes('already exists')) {
          // Extract company name from error message
          const nameMatch = validationResult.error.match(/"([^"]+)"/);
          const companyName = nameMatch ? nameMatch[1] : value;
          
          setCheckResult({
            exists: true,
            organization: { id: '', name: companyName, created_at: '' },
            message: validationResult.error
          });
          onDuplicateDetected?.(true);
        } else {
          setCheckResult({ exists: false });
          onDuplicateDetected?.(false);
        }
      } catch (error) {
        logger.error('Duplicate check failed:', error);
        setCheckResult(null);
        onDuplicateDetected?.(false);
      } finally {
        setIsChecking(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [value, onDuplicateDetected]);

  return (
    <div className="space-y-2">
      <Label htmlFor="company_name">What's your company name?</Label>
      
      <div className="relative">
        <Input
          id="company_name"
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g., Acme Corporation"
          className={checkResult?.exists ? 'border-warning' : ''}
        />
        
        {isChecking && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
        
        {!isChecking && checkResult && !checkResult.exists && value.trim().length >= 3 && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <CheckCircle className="h-4 w-4 text-success" />
          </div>
        )}
      </div>

      {checkResult?.exists && (
        <Alert className="bg-warning/10 border-warning/20">
          <AlertCircle className="h-4 w-4 text-warning" />
          <AlertDescription className="text-sm">
            <p className="font-medium text-foreground mb-1">This company name already exists</p>
            <p className="text-muted-foreground mb-2">
              A company with the name "{checkResult.organization?.name}" is already registered.
            </p>
            <p className="text-muted-foreground">
              If this is your company, please <a href={`mailto:${config.app.supportEmail}`} className="underline font-medium">contact support</a> to request access.
            </p>
          </AlertDescription>
        </Alert>
      )}

      {!checkResult?.exists && (
        <p className="text-xs text-muted-foreground">This will be used to create your company profile</p>
      )}
    </div>
  );
}
